import { useEffect, useMemo, useRef, useState } from 'react'
import { CollapsedNavTooltip } from '../CollapsedNavTooltip'
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom'
import {
  LayoutDashboard,
  Users,
  Briefcase,
  FileText,
  CalendarClock,
  FileBarChart,
  BarChart3,
  Menu,
  Plug,
  MessageCircle,
  LifeBuoy,
  Lightbulb,
  Sparkles,
  Key,
  FolderOpen,
  Gift,
  GitBranch,
  BookOpen,
  ShieldCheck,
  Activity,
  Mail,
  UserCog,
  Settings,
  History,
  ChevronDown,
  CreditCard,
  Lock,
  Layers,
  FolderTree,
  ClipboardList,
  CalendarDays,
  CalendarRange,
  Trophy,
  Medal,
  Target,
  Workflow,
  SlidersHorizontal,
  Handshake,
  Package,
} from 'lucide-react'
import { Logo } from '../Logo'
import { ErrorBoundary } from '../ErrorBoundary'
import { BrandMarkIcon } from '../BrandMarkIcon'
import { UserMenu } from '../UserMenu'
import { OrganisationSwitcher } from '../OrganisationSwitcher'
import { NotificationsBell } from '../NotificationsBell'
import { FontSizeMenu } from '../FontSizeMenu'
import { ThemeToggle } from '../ui/ThemeToggle'
import { DateTimeClock } from '../DateTimeClock'
import { SpacesModal } from '../SpacesModal'
import { CompanyTimezonePrompt } from '../CompanyTimezone'
import { AdminSpacesTree } from './AdminSpacesTree'
import { AdminCurrentSpaceTree } from './AdminCurrentSpaceTree'
import { usePersonalSpaces } from '../../lib/personal-spaces'
import { CompleteProfileModal } from '../CompleteProfileModal'
import { ProductTour } from '../ProductTour/ProductTour'
import { useTourStore } from '../ProductTour/useProductTour'
import { SidebarToggle } from './SidebarToggle'
import { useAuthStore } from '../../stores/authStore'
import { useSidebarCollapsed, useSidebarStore } from '../../stores/sidebarStore'
import { startRazorpayCheckout } from '../../lib/payments'
import { formatMoney } from '../../lib/format'
import { toast } from 'sonner'

/** Sidebar nav for Company Admins, ordered by **prerequisites before
 *  dependents** at the top and **frequency of use** below.
 *
 *  Why this order: a brand-new tenant has no Workspaces, Members or
 *  Departments — so Task Monitor / Work Reports / Performance are
 *  empty until those are set up. Putting setup primitives first means
 *  the menu reads as a sensible day-one checklist AND a steady-state
 *  tool palette. Items lower in the list are touched less often.
 *
 *  Bands (informational — not rendered as section headers):
 *    1     Landing                  Dashboard
 *    2     Administration group     Departments, Work Roles, Members
 *    3     Configuration group      Workflows, Report Schedule, Gamification
 *    4     Shared assets            Files & Links
 *    5     Process group            SOPs Library, Task Monitor, Work Reports
 *    6     Leaderboard              (top-level)
 *    7     AI Usage                 (top-level)
 *    8     Logs group               Audit Logs, Login Activity, Email Status
 *    9     Power tools group        Integrations (gated), API Keys
 *    10    Owner-only (top-level)   Billing, Support tickets
 *
 *  Administration holds org SETUP (Workspaces / Departments / Roles /
 *  Members); Configuration holds tenant TUNING (Workflow rules, Report
 *  cadence, Gamification); Process holds where the team EXECUTES (SOPs,
 *  Task Monitor's live view, Work Reports). Leaderboard is a top-level
 *  VIEW. The standalone Operations + Performance dropdowns were retired
 *  on 2026-06-19 in favour of this split.
 *
 *  The Administration band is rendered as a single collapsible dropdown
 *  ("Administration") so the four organisation-setup primitives sit under
 *  one tidy header rather than as four separate top-level rows. This
 *  matches the user-requested pattern (Vega-style sidebar) and frees
 *  vertical space for the more frequently-used Files / Task Monitor /
 *  Reports rows below.
 *
 *  Files & Links sits right below the Admin group — high-frequency for
 *  company admins who use TaskMagix as the team's shared asset library and
 *  want attachments/external links one click away from the org structure.
 *
 *  Items can declare `requires: 'feature_flag'` to hide unless the
 *  tenant has that feature on its plan. Integrations is gated on the
 *  `sop_integration` flag — only customers on the TaskMagix + ProcessPro
 *  bundle see the entry. */
// 2026-05-18 — restored Administration dropdown. Onboarding sequence
// (Workspaces → Departments → Work Role Management → Members) lives
// under one collapsible header again so the day-one setup primitives
// stay clustered. The four items are tightly related and shouldn't
// each consume a top-level sidebar slot.
// Per-item Sub Admin operation gate.
//   • `op: <slug>` — leaf shown only when `hasSubAdminOperation(slug)` is true.
//     A Company Admin passes the check by role, so Company Admins see every
//     row regardless of slug.
//   • `caOnly: true` — leaf shown ONLY when `role === 'company_admin'`. Sub
//     Admins can never see it even if granted every operation slug (Billing,
//     API Keys, etc. stay tenant-owner-only by product rule).
//   • No `op` and no `caOnly` — always shown to anyone in this layout
//     (Dashboard, Files & Links, AI Usage, Support tickets).
// Group nodes get the same `op` field on each child; the group header is
// hidden in the render path below when zero children are visible.
const navItems = [
  // Landing — first stop every login, has setup reminders in empty state.
  { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, end: true },
  // Notifications has NO sidebar row — it's reached from the header bell's
  // "View all notifications" link. (Removed by request across all panels.)
  // Administration dropdown group. Onboarding sequence reads top-down:
  // create Workspace → define Departments → define custom Roles →
  // invite Members into them. Keeping all four under one header keeps
  // the setup mental-model tidy and the sidebar shorter.
  {
    kind: 'group',
    key: 'administration',
    label: 'Administration',
    icon: Settings,
    children: [
      // No "Workspaces" entry — TaskMagix is single-Workspace, so there's
      // nothing to manage here. The `/workspaces` route stays mounted (for
      // any legacy multi-Workspace tenant + deep links) but is intentionally
      // absent from the sidebar. See the single-workspace de-emphasis.
      { to: '/departments', label: 'Departments', icon: Briefcase, end: false, op: 'departments.manage' },
      {
        to: '/work-roles',
        label: 'Work Roles',
        icon: UserCog,
        end: false,
        op: 'departments.manage',
      },
      { to: '/members', label: 'Members', icon: Users, end: false, op: 'members.manage' },
      // External Collaborators — plan-gated (off by default) via
      // `requires: 'external_collaboration'`; shares the members-management
      // operation gate. Sits right after Members as a member-adjacent surface.
      {
        to: '/external-collaborators',
        label: 'External Collaborators',
        icon: Handshake,
        end: false,
        op: 'members.manage',
        requires: 'external_collaboration',
      },
      // The standalone "Team" entry was folded into Members on
      // 2026-06-08 — Members now offers a List ⇄ Grid view toggle,
      // with Grid carrying the per-member task signals the Team
      // page used to surface. `/team` redirects to
      // `/members?view=grid` in App.jsx so any old bookmark still
      // lands on the equivalent view.
    ],
  },
  // Spaces tree — tenant-wide collapsible workspace + space tree.
  // Sits below the Administration group (moved here 2026-05-22 per
  // user request) so the day-one setup primitives stay at the top
  // and the day-to-day work surface follows underneath. Rendered as
  // a sentinel item (`kind: 'spacesTree'`) that the render switch
  // below resolves to the `<AdminSpacesSidebar />` component. Gated
  // on `workspaces.manage` so Sub Admins who don't hold the grant
  // don't see a tree they can't read; Company Admin always passes
  // the grant check via the role short-circuit.
  //
  // Two SEPARATE sidebar entries: "Spaces" (the always-visible inline
  // tree, like the User shell) and "Space Explorer" (the department →
  // space picker modal). Both gated on the same grant.
  { kind: 'spacesInline', key: 'spaces-inline', op: 'workspaces.manage' },
  { kind: 'spacesTree', key: 'spaces-tree', op: 'workspaces.manage' },
  // Configuration — tenant-level TUNING surfaces grouped in their own
  // dropdown: Workflows (task status flow), Report Schedule (report
  // cadence), and Gamification (scoring rules). Distinct from
  // Administration (org SETUP) and Process (where the team EXECUTES —
  // Task Monitor lives there). Replaced the retired Operations +
  // Performance dropdowns on 2026-06-19; Leaderboard became a top-level
  // row (it's a VIEW, not a config surface).
  {
    kind: 'group',
    key: 'configuration',
    label: 'Configuration',
    icon: SlidersHorizontal,
    children: [
      { to: '/workflows', label: 'Workflows', icon: GitBranch, end: false, op: 'workflows.manage', requires: 'workflows' },
      // Custom Fields was removed from the Company Admin panel. The backend
      // (`/admin/custom-fields` CRUD, the `custom_fields.manage` op) is
      // deliberately left in place: the op is the router-level gate for the
      // WHOLE adminModulesRouter (task import/export ride on it), and existing
      // definitions still render on task dialogs via `/task-custom-field-defs`.
      // Re-enabling the page is this line plus its route in App.jsx.
      { to: '/reminders-schedule', label: 'Reminders & Schedule', icon: CalendarClock, end: false, op: 'work_reports.view' },
      { to: '/usage-report', label: 'Usage Report', icon: FileBarChart, end: false, op: 'work_reports.view' },
      { to: '/gamification-settings', label: 'Gamification', icon: Trophy, end: false, caOnly: true, requires: 'gamification' },
    ],
  },
  // Shared assets — TaskMagix doubles as the team's file/link library
  // for the company admin, so this lives high up rather than in the
  // governance band. Stays flat (high-frequency, no obvious peers to
  // group with).
  { to: '/files-links', label: 'Files & Links', icon: FolderOpen, end: false },
  // Process — execution surfaces grouped together: SOPs (the
  // content spine), Task Monitor (live task view across the team),
  // Work Reports (filed reports). Workflows was moved OUT of this
  // group into Operations on 2026-06-08 — Workflows defines HOW
  // process runs, while these three are where the team actually
  // EXECUTES the process. Splitting "define" vs "execute" matches
  // the user's mental model better than the previous SOPs+Workflows
  // pairing.
  {
    kind: 'group',
    key: 'process',
    label: 'Process',
    icon: Workflow,
    children: [
      { to: '/sops-library', label: 'SOPs Library', icon: BookOpen, end: false, op: 'sops.manage', requires: 'sop_integration' },
      { to: '/task-monitor', label: 'Task Monitor', icon: ClipboardList, end: false, op: 'task_monitor.view' },
      { to: '/work-reports', label: 'Work Reports', icon: FileText, end: false, op: 'work_reports.view' },
      { to: '/analytics', label: 'Analytics', icon: BarChart3, end: false, op: 'analytics.view' },
    ],
  },
  // Leaderboard — promoted to a top-level row 2026-06-19 (was inside the
  // retired "Performance" dropdown alongside Gamification, which moved into
  // the Administration group). Read-only org ranking, same one employees
  // see, so admins can monitor it from their own panel. Plan-gated on the
  // gamification feature.
  // Goals — the unified Weekly + Monthly goals page (/app/goals, shared via
  // RoleAwareLayout so admins keep their own chrome). Available to Company
  // Admins AND Sub Admins so they can plan / assign / track goals from their
  // own panel; plan-gated on `weekly_goals`.
  { to: '/app/goals', label: 'Goals', icon: Target, end: false, requires: 'weekly_goals' },
  // Personal Reports — the admin files their OWN Daily / Weekly / Monthly work
  // report, exactly like employees do (UserLayout's "Reports" section). The
  // routes live under /app (shared via RoleAwareLayout) and the submit endpoint
  // is auth-only, so the CA could always reach the form — this just surfaces
  // it. Feeds the report-based figures (Hours / Meetings / Days) in the Goals
  // "Actual Work" panel, which were otherwise always 0 for an admin. Ungated,
  // mirroring UserLayout ("every role submits their own reports").
  {
    kind: 'group',
    key: 'reports',
    label: 'Reports',
    icon: FileBarChart,
    children: [
      { to: '/app/reports/daily', label: 'Daily Report', icon: ClipboardList, end: false },
      { to: '/app/reports/weekly', label: 'Weekly Report', icon: CalendarDays, end: false },
      { to: '/app/reports/monthly', label: 'Monthly Report', icon: CalendarRange, end: false },
      // Report Settings — configure the Task Type / Item Status dropdown
      // catalogues for the report forms. Shown to Company Admin (role
      // short-circuit) and to a Sub Admin / manager with workspace- or
      // department-wide reports access, mirroring the backend assertCanEdit and
      // UserLayout's canManageReportSettings. Was previously reachable only in
      // the employee (UserLayout) nav, so admins lost it under RoleAwareLayout.
      {
        // Matches the page header icon + the UserLayout (employee) link — the
        // Report Settings gear is the feature's canonical icon everywhere.
        to: '/app/report-settings',
        label: 'Report Settings',
        icon: Settings,
        end: false,
        anyOp: ['reports.view_workspace', 'reports.view_department', 'pages.team_reports'],
      },
    ],
  },
  { to: '/leaderboard', label: 'Leaderboard', icon: Medal, end: false, requires: 'gamification' },
  // AI Usage — singleton insight view (current month's token spend
  // by tenant). Stays flat because grouping a single-child entry is
  // worse UX than leaving it where it is.
  //
  // Opened to Sub Admins 2026-05-22. The backend route is now
  // `requireAdminOp('ai_usage.view')`, and every Sub Admin gets the
  // slug auto-granted via the binary catalogue. Company Admin always
  // passes via the role short-circuit inside hasSubAdminOperation.
  { to: '/ai-usage', label: 'AI Usage', icon: Sparkles, end: false, op: 'ai_usage.view', requires: 'ai_usage' },
  // Logs — Audit Log, Login Activity and Email Status Logs share one
  // collapsible dropdown so the three read-only history surfaces sit
  // together, mirroring the Super Admin sidebar's "Logs" dropdown. All
  // three gate on `audit_log.view`, so the whole group shows or hides as
  // one for a Sub Admin without the grant (the empty-group case is
  // dropped by the render-path filter).
  {
    kind: 'group',
    key: 'logs',
    label: 'Logs',
    icon: History,
    children: [
      { to: '/audit-log', label: 'Audit Log', icon: ShieldCheck, end: false, op: 'audit_log.view' },
      // Login Activity Logs — read-only listing with Online/Offline
      // status per user, last login/logout/IP. Tenant-scoped server-side.
      { to: '/login-activity', label: 'Login Activity', icon: Activity, end: false, op: 'audit_log.view' },
      // Email Status Logs — tenant-scoped feed of outbound emails
      // (welcome, invitation, password-reset, billing, etc.) with status,
      // failure reason, and a one-click retry.
      { to: '/email-logs', label: 'Email Status Logs', icon: Mail, end: false, op: 'audit_log.view' },
    ],
  },
  // Integrations — platform/external connections. SOP Integration is a
  // paid add-on (ProcessPro connector); API Keys are tenant-owner-only
  // credential storage for BYO AI providers. Both are Company-Admin-only
  // gated at the router level, so the whole group hides for Sub Admins
  // who don't hold either grant.
  //
  // SOP Integration is plan-gated via `requires: 'sop_integration'`:
  // the entry is hidden (and the route blocked) for any tenant whose
  // plan doesn't include the flag, so orgs without the add-on don't
  // see a configurator they can't use.
  //
  // `caOnly` because the backend `/admin/*` routes powering this surface
  // (custom fields, CSV import/export, exit-handover commit — see
  // `admin-modules.routes.js`) sit behind `requireRole('company_admin')`
  // at the router-level mount.
  {
    kind: 'group',
    key: 'integrations',
    label: 'Integrations',
    icon: Plug,
    children: [
      {
        to: '/sop-integration',
        label: 'SOP Integration',
        icon: Plug,
        end: false,
        caOnly: true,
        requires: 'sop_integration',
      },
      {
        to: '/telegram-integration',
        label: 'Telegram',
        icon: MessageCircle,
        end: false,
        caOnly: true,
      },
      { to: '/api-keys', label: 'AI Provider Keys', icon: Key, end: false, caOnly: true, requires: 'api_access' },
    ],
  },
  // Billing — tenant-owner-only. Promoted out of the old "Account"
  // dropdown on 2026-06-08; the dropdown only held Billing and
  // Support tickets and the extra wrapping click added friction
  // without grouping anything semantically related. Now sits as
  // its own top-level row at the foot of the rail.
  //
  // CRITICAL — keep this as a top-level leaf (not inside a group).
  // The billing-lock UI logic below (line ~970) singles Billing out
  // via `item.to === '/billing'` so it stays clickable while every
  // other entry is greyed out on a locked tenant. When Billing was
  // nested inside the Account group, that single-out check matched
  // the group instead of the leaf — leaving Billing unreachable on
  // a locked tenant. The flattening also fixes that latent defect.
  { to: '/billing', label: 'Billing', icon: CreditCard, end: false, caOnly: true },
  // Refer & Earn — company growth surface, sibling of Billing. Company-Admin
  // only (like Billing); the referral link is the organisation's own.
  { to: '/referrals', label: 'Refer & Earn', icon: Gift, end: false, caOnly: true },
  // [Servexa] Support Tickets — the ACTIVE support flow (spec A6), submitted to
  // Servexa via the tenant `/servexa/tickets` API (jwtAuth). Kept at
  // `/support-tickets` so existing deep-links + this nav slot stay stable.
  // Spec A20: Support before Feature Suggestions. `caOnly` surfaces it to
  // company_admin + sub_admin (not in the deny-list below).
  { to: '/support-tickets', label: 'Support Tickets', icon: LifeBuoy, end: false, caOnly: true },
  // [Servexa] Feature Suggestions — a SEPARATE flow from Support (spec A7),
  // never merged into one "feedback" surface.
  { to: '/feature-suggestions', label: 'Feature Suggestions', icon: Lightbulb, end: false, caOnly: true },
  // Our Products — the cross-product catalogue, same page every
  // customer sees. Sits at the very foot of the rail; flat, no op gate
  // (visible to all admins).
  { to: '/app/our-products', label: 'Our Products', icon: Package, end: false },
  // User Manual — the role-based guide. Last item in the rail (kept below
  // Our Products); no gate, visible to every admin. Routed under the shared
  // /app RoleAwareLayout so it keeps the admin chrome.
  { to: '/app/user-manual', label: 'User Manual', icon: BookOpen, end: false },
]

/** Deny-list of `caOnly` routes that stay company_admin-only even for
 *  Sub Admins. Sub Admins otherwise share the full Company Admin panel,
 *  but these three remain tenant-owner-only because the backend blocks
 *  them for Sub Admins (a 403), so hiding the rows keeps the sidebar
 *  honest. Everything else flagged `caOnly` (Gamification, Support
 *  tickets) surfaces for Sub Admins. */
const CA_ONLY_FOR_SUB_ADMIN = ['/billing', '/api-keys', '/sop-integration']

/** Single sidebar leaf — visual identical for top-level and nested
 *  rows. Top-level rows render as `NavLink` so the global
 *  `aside [aria-current='page']::before` rule paints the accent bar.
 *  Nested rows (children of a `GroupNode`) deliberately render as a
 *  plain `Link` so the bar is NOT painted on the child — the parent
 *  group header carries the bar instead. Active styling on the child
 *  is still applied via the local `isActive` check; it's just the
 *  vertical accent indicator that moves up to the parent. */
function LeafLink({ to, label, icon: Icon, end, nested = false, collapsed = false }) {
  const location = useLocation()
  // `wrapperRef` anchors the portal-rendered collapsed-mode tooltip.
  // Declared HERE (before any early return) so the hook order stays
  // stable — Rules of Hooks.
  const wrapperRef = useRef(null)
  // Nested rows (children of an expanded GroupNode) don't make sense
  // when the sidebar is collapsed — the parent group has already
  // hidden its children. Skip them entirely so the icon column stays
  // tight.
  if (nested && collapsed) return null
  // Active and hover treatments match the SuperAdmin sidebar polish:
  //   • Active row: translucent white background pill, white label +
  //     icon, inset highlight ring along the top edge, 2.25 stroke
  //     weight on the icon for emphasis.
  //   • Hover row: soft white wash, label + icon flip to white. The
  //     0.5px slide-right was removed so flat rows and dropdown
  //     section headers feel identical on hover — the slide only
  //     applied to the flat rows, which made the sidebar feel
  //     inconsistent ("some rows jump, others don't").
  const activeClasses =
    'bg-[color:var(--color-accent-bg-strong)] text-white font-semibold shadow-[inset_0_1px_0_rgba(255,255,255,0.18)]'
  const inactiveClasses =
    'text-[color:var(--color-ink-muted)] hover:bg-[color:var(--color-card-hover)] hover:text-white'

  if (nested) {
    const isActive = end
      ? location.pathname === to
      : location.pathname === to || location.pathname.startsWith(`${to}/`)
    return (
      <Link
        to={to}
        className={
          `group flex items-center gap-3 rounded-[10px] py-2.5 pl-9 pr-3 transition-all cursor-pointer ${
            isActive ? activeClasses : inactiveClasses
          }`
        }
      >
        <Icon
          size={18}
          className={
            isActive
              ? 'text-white shrink-0'
              : 'text-[color:var(--color-ink-muted)] group-hover:text-white shrink-0 transition-colors'
          }
          strokeWidth={isActive ? 2.25 : 1.75}
        />
        {label}
      </Link>
    )
  }
  // Portal-rendered tooltip when the sidebar is collapsed escapes
  // the parent `<nav>`'s overflow clipping context (which forces
  // `overflow-x: auto` whenever `overflow-y: auto` is set on the same
  // element), so the tooltip can extend past the 68px-wide rail into
  // the main content area without being clipped. The ref was declared
  // up top so Rules of Hooks stays satisfied across the early return.
  return (
    <div ref={wrapperRef}>
      <NavLink
        to={to}
        end={end}
        // `aria-label` (not `title`) when collapsed — gives screen
        // readers the accessible name without triggering the browser's
        // native ~1.5s-delay tooltip, which would double up with the
        // portal tooltip below. When expanded the visible label text
        // already provides accessibility.
        aria-label={collapsed ? label : undefined}
        className={({ isActive }) =>
          `group flex items-center gap-3 rounded-[10px] transition-all cursor-pointer ${
            collapsed ? 'px-0 py-2.5 justify-center' : 'px-3 py-2.5'
          } ${isActive ? activeClasses : inactiveClasses}`
        }
      >
        {({ isActive }) => (
          <>
            <Icon
              size={18}
              className={
                isActive
                  ? 'text-white shrink-0'
                  : 'text-[color:var(--color-ink-muted)] group-hover:text-white shrink-0 transition-colors'
              }
              strokeWidth={isActive ? 2.25 : 1.75}
            />
            {collapsed ? null : label}
          </>
        )}
      </NavLink>
      {collapsed ? <CollapsedNavTooltip label={label} anchorRef={wrapperRef} /> : null}
    </div>
  )
}


/** Collapsible group header. Auto-expands on initial mount when the
 *  current URL matches one of the children; thereafter the admin's
 *  manual toggle wins. Active state on the header turns on when any
 *  child route is the current location, so an admin sitting on
 *  /workspaces knows which band that page belongs to. */
function GroupNode({ group, currentPath, collapsed = false, open, onToggle }) {
  // See `LeafLink` for the rationale — the portal tooltip needs an
  // anchor outside the `<nav>`'s clipped overflow context.
  const wrapperRef = useRef(null)
  const childPaths = useMemo(
    () => group.children.map((c) => c.to),
    [group.children],
  )
  const containsActive = useMemo(
    () =>
      childPaths.some(
        (p) => currentPath === p || currentPath.startsWith(`${p}/`),
      ),
    [childPaths, currentPath],
  )
  // Open / closed state is owned by the layout so the groups behave as
  // an accordion (only one open at a time) — `open` + `onToggle` are
  // passed in.
  const setSidebarCollapsed = useSidebarStore((s) => s.set)
  const Icon = group.icon

  return (
    <div ref={wrapperRef}>
      <button
        type="button"
        // When collapsed, this header acts as "wake up the sidebar so
        // I can see the group's children" — clicking it expands the
        // whole rail back to full width. When expanded, it does its
        // usual job of toggling the children open/closed.
        onClick={
          collapsed ? () => setSidebarCollapsed(false) : onToggle
        }
        // No `title` — would double up with the portal tooltip on
        // hover. `aria-label` gives screen readers the accessible
        // name (with the "click to expand" hint baked in) without a
        // native tooltip.
        aria-label={
          collapsed ? `${group.label} — click to expand sidebar` : undefined
        }
        aria-expanded={collapsed ? undefined : open}
        aria-controls={collapsed ? undefined : `nav-group-${group.key}`}
        // `aria-current="page"` is what the global
        // `aside [aria-current='page']::before` CSS rule keys off to
        // paint the accent vertical bar. We set it on the GROUP
        // HEADER (not the active child) so the indicator sits at the
        // top-level row — Workspaces / Departments / etc. underneath
        // share the group's bar rather than each painting their own.
        // Matches the user-panel sidebar's `SidebarSection`.
        aria-current={containsActive ? 'page' : undefined}
        // Header treatment mirrors the user-panel sidebar's
        // `SidebarSection` exactly: inactive header reads as a section
        // title (`ink-strong`), active header flips to the accent
        // colour with semibold weight. Chevron rotates 0deg / -90deg
        // on open / closed — same animation pattern. Keeps Company
        // Admin + user panels visually consistent on the dropdown
        // active state.
        className={`group w-full flex items-center gap-3 rounded-[10px] transition-all cursor-pointer ${
          collapsed ? 'px-0 py-2.5 justify-center' : 'px-3 py-2.5'
        } ${
          containsActive
            ? 'bg-[color:var(--color-accent-bg-strong)] text-white font-semibold shadow-[inset_0_1px_0_rgba(255,255,255,0.18)]'
            : 'text-[color:var(--color-ink-strong)] hover:bg-[color:var(--color-card-hover)] hover:text-white'
        }`}
      >
        <Icon
          size={18}
          className={
            containsActive
              ? 'text-white shrink-0'
              : 'group-hover:text-white shrink-0 transition-colors'
          }
          strokeWidth={containsActive ? 2.25 : 1.75}
        />
        {collapsed ? null : (
          <>
            <span className="flex-1 text-left">{group.label}</span>
            <ChevronDown
              size={14}
              className={`shrink-0 transition-transform duration-150 ${open ? 'rotate-0' : '-rotate-90'}`}
              aria-hidden="true"
            />
          </>
        )}
      </button>
      {/* Collapsed-sidebar hover tooltip — same portal-based
       *  treatment as `LeafLink`. Floats to the right of the icon-only
       *  group header so the user can identify the band without
       *  waking the sidebar back up. */}
      {collapsed ? <CollapsedNavTooltip label={group.label} anchorRef={wrapperRef} /> : null}
      {open && !collapsed ? (
        <div id={`nav-group-${group.key}`} className="mt-1 space-y-1">
          {group.children.map((child) => (
            <LeafLink key={child.to} {...child} nested />
          ))}
        </div>
      ) : null}
    </div>
  )
}

/** A greyed-out, non-interactive sidebar leaf — shown for every nav
 *  item except Billing while the tenant is billing-locked. */
function DisabledLeaf({ label, icon: Icon, collapsed = false }) {
  return (
    <div
      aria-disabled="true"
      title={collapsed ? `${label} — complete payment to unlock` : 'Complete payment to unlock'}
      className={
        'flex items-center gap-3 rounded-[10px] border border-transparent text-[color:var(--color-ink-subtle)] opacity-50 cursor-not-allowed select-none ' +
        (collapsed ? 'px-0 py-2.5 justify-center' : 'px-3 py-2.5')
      }
    >
      <Icon size={18} />
      {collapsed ? null : label}
    </div>
  )
}

/** Greyed-out group header — collapsed and non-interactive while locked. */
function DisabledGroup({ group, collapsed = false }) {
  const Icon = group.icon
  return (
    <div
      aria-disabled="true"
      title={collapsed ? `${group.label} — complete payment to unlock` : 'Complete payment to unlock'}
      className={
        'flex items-center gap-3 rounded-[10px] text-[color:var(--color-ink-subtle)] opacity-50 cursor-not-allowed select-none ' +
        (collapsed ? 'px-0 py-2.5 justify-center' : 'px-3 py-2.5')
      }
    >
      <Icon size={18} />
      {collapsed ? null : <span className="flex-1 text-left">{group.label}</span>}
    </div>
  )
}

/** Full-area overlay shown when the tenant hasn't paid for its paid
 *  plan. Dims the dashboard and surfaces the Pay-Now modal. */
function BillingLockOverlay({ billing, user }) {
  const navigate = useNavigate()
  const refreshUser = useAuthStore((s) => s.refreshUser)
  const [busy, setBusy] = useState(false)

  const amountLabel =
    billing?.amount != null
      ? formatMoney(billing.amount, billing.currency || 'USD', { decimals: 0 })
      : null

  function payNow() {
    setBusy(true)
    void startRazorpayCheckout({
      planCode: billing.planCode,
      cadence: 'monthly',
      user,
      onSuccess: async () => {
        await refreshUser()
      },
      onClose: () => setBusy(false),
    })
  }

  return (
    <div className="absolute inset-0 z-40 flex items-center justify-center p-6 bg-[color:var(--color-ink-strong)]/45 backdrop-blur-[2px]">
      <div className="w-full max-w-md rounded-2xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] shadow-2xl p-7 text-center">
        <span className="mx-auto mb-4 grid place-items-center h-14 w-14 rounded-full bg-[color:var(--color-accent-bg)] border border-[color:var(--color-accent-border)]">
          <Lock size={24} className="text-[color:var(--color-accent-strong)]" />
        </span>
        <h2 className="font-display text-2xl text-[color:var(--color-ink-strong)]">
          Complete Your Payment
        </h2>
        <p className="mt-2 text-sm text-[color:var(--color-ink-muted)]">
          {user?.role === 'company_admin'
            ? 'Please process the payment first to unlock full access to your company.'
            : 'Your company’s access is locked until the payment is completed.'}
        </p>

        {billing?.planName ? (
          <div className="mt-5 flex items-center justify-between gap-3 rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] px-4 py-3 text-left">
            <div>
              <p className="text-xs uppercase tracking-wider font-semibold text-[color:var(--color-ink-muted)]">
                Current Plan
              </p>
              <p className="text-sm font-semibold text-[color:var(--color-ink-strong)]">
                {billing.planName}
              </p>
            </div>
            {amountLabel ? (
              <p className="font-display text-xl text-[color:var(--color-ink-strong)] whitespace-nowrap">
                {amountLabel}
                <span className="text-xs font-normal text-[color:var(--color-ink-muted)]">
                  {' '}
                  /month
                </span>
              </p>
            ) : null}
          </div>
        ) : null}

        {/* Payment is Company-Admin-only: the /billing route is a
         *  `CompanyAdminRoute` and the /billing/checkout API is
         *  `requireRole('company_admin')`. So only Company Admins get the Pay
         *  Now + "View all plans" actions; a Sub Admin on a locked tenant would
         *  hit a 403 on Pay Now and a Dashboard bounce on the Billing link, so
         *  they get a clear "ask your Company Admin" note instead. */}
        {user?.role === 'company_admin' ? (
          <>
            <button
              type="button"
              onClick={payNow}
              disabled={busy}
              className="mt-6 w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-semibold bg-[color:var(--color-primary)] text-white hover:bg-[color:var(--color-primary-hover)] cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <CreditCard size={16} />
              {busy ? 'Processing…' : 'Pay Now with Razorpay'}
            </button>
            <button
              type="button"
              onClick={() => navigate('/billing')}
              className="mt-2.5 text-xs text-[color:var(--color-accent-strong)] hover:underline cursor-pointer"
            >
              View All Plans on the Billing Page
            </button>
          </>
        ) : (
          <p className="mt-6 rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] px-4 py-3 text-sm text-[color:var(--color-ink-muted)]">
            Only your Company Admin can complete this payment. Please ask them to
            restore your organisation’s access.
          </p>
        )}
      </div>
    </div>
  )
}

export function AdminLayout() {
  const { user, tenant, signOut } = useAuthStore()
  const hasSubAdminOperation = useAuthStore((s) => s.hasSubAdminOperation)
  const refreshUser = useAuthStore((s) => s.refreshUser)
  const navigate = useNavigate()
  const location = useLocation()
  const billing = tenant?.billing ?? null
  const locked = Boolean(billing?.locked)
  // Plan-selection gate. Mirrors the `<PlanGate>` in App.jsx — a
  // Company Admin who landed via Google SSO has a tenant with
  // `planSelectedAt: null` until they pick a plan on the Billing
  // page. Until that happens, the sidebar nav is rendered in a
  // visually-disabled state (dimmed + click-blocked) so the user
  // can't navigate anywhere except Billing. The route guard already
  // bounces them back; this just gives them a coherent UI cue
  // instead of a flash-and-bounce experience.
  //
  // Sub Admins / company admins of plan-active tenants are unaffected
  // (planSelectedAt non-null on every tenant outside the brand-new
  // Google bootstrap window — see the backfill seed script in
  // `src/seed/backfill-tenant-plan-selected-at.js`).
  const planGated =
    user?.role === 'company_admin' && tenant && !tenant.planSelectedAt
  // While the product tour runs, force the rail EXPANDED (a collapsed icon-only
  // rail renders no group children). Dropdown groups stay CLOSED and open one at
  // a time as the tour reaches them: `tourActiveGroup` is the group the current
  // step lives in (null = all closed).
  const tourActive = useTourStore((s) => s.phase !== 'idle')
  const tourActiveGroup = useTourStore((s) => s.activeGroup)
  const collapsed = useSidebarCollapsed() && !tourActive
  // Mobile off-canvas drawer state (shared store). Auto-closes on navigation.
  const mobileOpen = useSidebarStore((s) => s.mobileOpen)
  const openMobile = useSidebarStore((s) => s.openMobile)
  const closeMobile = useSidebarStore((s) => s.closeMobile)
  const { pathname } = useLocation()
  const mainRef = useRef(null)
  useEffect(() => {
    closeMobile()
    // Reset the scroll container to the top on navigation. The page scroll
    // lives on <main> (the shell is `h-screen … overflow-hidden`), NOT on
    // the window, so `window.scrollTo` would be a no-op here.
    //
    // Without this a deep-scrolled page carries its offset into the next
    // route — click a quick action at the bottom of the Dashboard and you
    // land at the bottom of Members instead of its start (QA-reported).
    // SuperAdminLayout and UserLayout already did this; AdminLayout had the
    // effect but only the `closeMobile()` half of it.
    //
    // Keyed on `pathname` alone, deliberately: a search / filter change is
    // a same-page update, and resetting scroll there would fight the
    // `?focusTask=` deep links that scroll an item into view.
    mainRef.current?.scrollTo({ top: 0, left: 0 })
  }, [pathname, closeMobile])

  // Open / closed state for the Spaces modal. The sidebar "Spaces"
  // entry was a deep nested tree until 2026-05-25; replaced by a
  // single button that opens this modal so the rail stays clean
  // past 100+ spaces. `AdminSpacesSidebar` is still in the repo
  // (unused) so the old tree can be re-enabled via a one-line swap
  // if the modal pattern doesn't stick.
  const [spacesModalOpen, setSpacesModalOpen] = useState(false)
  // Open / closed state for the standalone "Spaces" inline-tree section
  // (separate from the "Space Explorer" modal entry). Defaults CLOSED — the
  // admin expands it when they want their personal Spaces.
  const [spacesInlineOpen, setSpacesInlineOpen] = useState(false)

  // Which Spaces menu owns the currently-open Space? A personal Space (opened
  // from "Spaces") must light up "Spaces", NOT "Space Explorer" — and vice
  // versa for a department Space. Derived from the user's personal-space ids.
  const { personalSpaces, personalSpaceIds } = usePersonalSpaces()
  const activeSpaceId =
    location.pathname.match(/^\/app\/spaces\/([^/]+)/)?.[1] ?? null
  const onAnySpace = location.pathname.startsWith('/app/spaces/')
  const onPersonalSpace = Boolean(
    activeSpaceId && personalSpaceIds.has(String(activeSpaceId)),
  )

  // Keep the Personal Spaces dropdown open whenever the current route is a
  // personal Space. Without this it collapses the moment you open a personal
  // list: navigating between /dashboard (its own <AdminLayout>) and the /app
  // Space surface (<RoleAwareLayout> → <AdminLayout>) REMOUNTS this layout, so
  // `spacesInlineOpen` resets to its initial `false` — the list opens but the
  // tree closes under you. Keyed on the active Space (not every render) so a
  // manual collapse is respected until the next navigation. Mirrors the
  // `openGroup` self-heal effect below. `personalSpaceIds` may load async after
  // a remount, so `onPersonalSpace` flips true a tick later and re-opens it.
  useEffect(() => {
    if (onPersonalSpace) setSpacesInlineOpen(true)
  }, [onPersonalSpace, activeSpaceId])

  // Accordion: only one sidebar group is open at a time. The open group
  // is tracked here in the parent (rather than each GroupNode owning its
  // own state) so expanding one collapses the others. Seeded to the
  // group that owns the current route, and the effect re-opens that
  // group whenever navigation lands inside a different one, so a deep
  // link arrives with its band already expanded.
  const groupKeyForPath = navItems
    .filter((i) => i.kind === 'group')
    .find((g) =>
      g.children.some(
        (c) =>
          location.pathname === c.to ||
          location.pathname.startsWith(`${c.to}/`),
      ),
    )?.key
  const [openGroup, setOpenGroup] = useState(groupKeyForPath ?? null)
  useEffect(() => {
    if (groupKeyForPath) setOpenGroup(groupKeyForPath)
  }, [groupKeyForPath])

  // Sub Admin live-sync — refetch /auth/me on window focus so any
  // grants the Company Admin changed in another tab (or another
  // browser entirely) take effect without a hard reload. The sidebar
  // + every `op:`-gated page reads from the auth-store user, so a
  // refresh here propagates into both. Scoped to Sub Admin to avoid
  // doubling network load for Company Admin, whose role/operations
  // can't be self-edited.
  useEffect(() => {
    if (user?.role !== 'sub_admin') return undefined
    function onFocus() {
      refreshUser().catch(() => {})
    }
    window.addEventListener('focus', onFocus)
    return () => window.removeEventListener('focus', onFocus)
  }, [user?.role, refreshUser])
  // The Billing page itself stays fully usable so the admin can pay.
  const showLock = locked && location.pathname !== '/billing'

  // Complete Profile gate — only applies inside the Company Admin
  // shell, and only to Company Admins (Sub Admins are invited by an
  // existing CA, so they never go through the Google bootstrap path
  // that flips profileComplete to false). The modal is mounted at
  // the bottom of this layout so its fixed positioning escapes the
  // sidebar's stacking context and blocks all clicks — sidebar links
  // included — until the user finishes the form.
  const needsCompleteProfile =
    user?.role === 'company_admin' &&
    user?.googleLinked === true &&
    user?.profileComplete === false

  /** Visibility filter for a single nav leaf, applied to both
   *  top-level rows and dropdown-group children. Returns `true` when
   *  the item should render for the current actor.
   *
   *    • `requires` items: visible only when the tenant's plan does
   *      NOT explicitly exclude the feature flag. Fail-open — items
   *      without `requires`, and tenants whose plan predates the
   *      flag (undefined), pass; only an explicit `false` hides.
   *      Mirrors the backend `requireFeature` + the store's
   *      `hasFeature`. CRITICAL that this check runs in
   *      `isItemVisible` (not just on the top-level array) so it
   *      also filters group children — e.g. hides "SOPs Library"
   *      inside the "Process" dropdown when the plan excludes
   *      `sop_integration`.
   *    • `caOnly` items: always visible to a Company Admin. Sub Admins
   *      share the Company Admin panel and so see ROUTINE caOnly items
   *      (Gamification, Support tickets) too — EXCEPT the three
   *      dangerous, tenant-owner-only routes in `CA_ONLY_FOR_SUB_ADMIN`
   *      (Billing, API Keys, SOP Integration), which the backend blocks
   *      for Sub Admins and therefore stay hidden from their sidebar.
   *    • `op` items: visible when `hasSubAdminOperation(op)` is true.
   *      Company Admin always passes that check by role.
   *    • Otherwise: visible (Dashboard, Files & Links, AI Usage are
   *      role-agnostic). */
  function isItemVisible(item) {
    if (item.requires && tenant?.features?.[item.requires] === false) {
      return false
    }
    if (item.caOnly) {
      if (user?.role === 'company_admin') return true
      // Sub Admins see routine caOnly items, but never the three
      // dangerous tenant-owner-only routes.
      if (user?.role === 'sub_admin') {
        return !CA_ONLY_FOR_SUB_ADMIN.includes(item.to)
      }
      return false
    }
    if (item.op) return hasSubAdminOperation(item.op)
    // `anyOp: [slugs]` — shown when the actor holds ANY of the operations (a
    // Company Admin passes every slug via the role short-circuit). Used for
    // Report Settings, whose backend permits workspace- OR department-wide
    // reports access (matching work-report-settings assertCanEdit).
    if (item.anyOp) return item.anyOp.some((op) => hasSubAdminOperation(op))
    return true
  }

  async function handleSignOut() {
    await signOut()
    toast.success('Signed out successfully.')
    navigate('/sign-in', { replace: true })
  }

  return (
    <div className="taskmagix-page-bg h-screen flex overflow-hidden">
      {/* Mobile drawer backdrop — taps to close. Hidden on desktop (lg+)
       *  where the sidebar is always in-flow. */}
      {mobileOpen ? (
        <div
          className="fixed inset-0 z-40 bg-black/40 lg:hidden"
          onClick={closeMobile}
          aria-hidden="true"
        />
      ) : null}
      {/* Sidebar — navy→cyan brand gradient. The `.taskmagix-sidebar`
       *  class scopes ink + rule tokens to white so every NavLink and
       *  divider lands legible on the dark gradient. */}
      <aside
        data-tour="app-sidebar"
        className={
          'taskmagix-sidebar shrink-0 border-r border-[color:var(--color-rule)] text-[color:var(--color-ink)] text-[length:calc(var(--font-size-scale,1)*14px)] font-medium flex flex-col ' +
          // Mobile: off-canvas drawer (fixed, slides in/out). Desktop (lg+):
          // restored to the exact in-flow sidebar via lg: resets, so desktop
          // is unchanged. Transition covers the drawer slide + collapse width.
          'fixed inset-y-0 left-0 z-50 lg:relative lg:inset-auto lg:z-auto ' +
          'transition-[transform,width] duration-200 ease-out ' +
          (mobileOpen ? 'translate-x-0 ' : '-translate-x-full ') +
          'lg:translate-x-0 ' +
          (collapsed ? 'w-[68px]' : 'w-[280px]')
        }
      >
        {/* Edge toggle — floating circle on the sidebar's right edge.
         *  Replaces the inline header toggles so the logo gets the
         *  full header width when expanded, and the collapsed
         *  68px-wide rail stops showing two icons stacked. */}
        <SidebarToggle placement="edge" />

        <div
          className={
            'relative h-[84px] flex items-center justify-center ' +
            (collapsed ? 'px-2' : 'px-4')
          }
        >
          {/* Collapsed rail keeps a fitted logo + go-home target — same
           *  treatment as UserLayout (see comment there). */}
          {collapsed ? (
            <Link
              to="/dashboard"
              aria-label="Go to Dashboard"
              className="inline-flex items-center justify-center rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)]/30 transition-opacity hover:opacity-90"
            >
              <BrandMarkIcon />
            </Link>
          ) : (
            <Link
              to="/dashboard"
              aria-label="Go to Dashboard"
              className="inline-flex items-center rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)]/30 transition-opacity hover:opacity-90"
            >
              <Logo size="xl" className="p-1.5" />
            </Link>
          )}
          {/* Bottom divider as an absolute-positioned line rather than
           *  a `border-b` on the container, so the line is INSET from
           *  the sidebar's left and right walls instead of spanning
           *  the full panel width. Matches the inset treatment the
           *  active accent bar uses. */}
          <span
            aria-hidden="true"
            className="pointer-events-none absolute left-4 right-4 bottom-0 h-px bg-[color:var(--color-rule)]"
          />
        </div>

        {/* Plan-gate locked banner — shown only when a brand-new
         *  Google-SSO Company Admin hasn't picked a plan yet. The
         *  nav itself is dimmed + click-blocked further down via the
         *  `planGated` className; this banner explains WHY so the
         *  greyed-out sidebar reads as intentional, not broken. */}
        {planGated && !collapsed ? (
          <div className="px-4 pt-4">
            <div className="rounded-lg border border-white/15 bg-white/5 px-3 py-2.5 text-[length:calc(var(--font-size-scale,1)*12px)] leading-snug text-white/85">
              <p className="font-semibold m-0">Pick a plan to unlock</p>
              <p className="mt-1 m-0 text-white/65">
                Choose any plan below to start using TaskMagix.
              </p>
            </div>
          </div>
        ) : null}
        {/* Same banner collapsed to a single lock-icon strip when
         *  the sidebar is in its 68px collapsed mode. */}
        {planGated && collapsed ? (
          <div className="px-2 pt-3" title="Pick a plan to unlock TaskMagix">
            <div className="h-9 w-9 mx-auto rounded-lg border border-white/15 bg-white/5 flex items-center justify-center text-white/70">
              <Lock size={16} />
            </div>
          </div>
        ) : null}

        <nav
          className={
            // Top fade-mask: rows fade to transparent before reaching the
            // header divider, so a scrolled list never reads as "touching"
            // the line under the logo. The fade height matches pt-6 (24px)
            // so at rest the first row sits clear of it and nothing looks
            // dimmed. Standing rule: this mask lives identically in all
            // three sidebar layouts (Admin / User / SuperAdmin).
            'flex-1 min-h-0 overflow-y-auto pt-6 pb-4 space-y-1.5 [mask-image:linear-gradient(to_bottom,transparent_0,black_24px,black_100%)] [-webkit-mask-image:linear-gradient(to_bottom,transparent_0,black_24px,black_100%)] [scrollbar-width:thin] [scrollbar-color:var(--color-rule)_transparent] [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-button]:hidden [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-[color:var(--color-rule)] [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-[color:var(--color-ink-muted)] ' +
            (collapsed ? 'px-2' : 'px-4') +
            // Plan-gate styling: dim the entire nav + block pointer
            // events so nothing inside is clickable. The locked
            // banner above explains the state; PlanGate in App.jsx
            // also enforces it at the route level if a determined
            // user types a URL directly.
            (planGated ? ' opacity-40 pointer-events-none select-none' : '')
          }
          aria-disabled={planGated ? 'true' : undefined}
        >
          {navItems
            // Apply ALL visibility gates (plan `requires`, Sub Admin
            // operation `op`, Company-Admin-only `caOnly`) in one pass
            // via `isItemVisible`. Groups are kept if AT LEAST ONE
            // child survives the per-leaf filter; children are
            // pre-filtered so `GroupNode` renders only the visible
            // subset and the empty-group case (every child gated out)
            // gets dropped entirely.
            .map((item) => {
              if (item.kind === 'group') {
                const visibleChildren = item.children.filter(isItemVisible)
                if (visibleChildren.length === 0) return null
                return { ...item, children: visibleChildren }
              }
              return isItemVisible(item) ? item : null
            })
            .filter(Boolean)
            .map((item) => {
              const isBilling =
                item.kind !== 'group' && item.to === '/billing'
              // The Spaces tree sentinel resolves to the
              // AdminSpacesSidebar component. While billing-locked it
              // collapses to a disabled placeholder so the admin
              // visually loses access until they pay (mirroring the
              // greyed-out treatment every other nav row gets).
              // "Spaces" — a standalone collapsible section holding the
              // always-visible inline tree (the same experience the User
              // shell gives). Separate menu entry from "Space Explorer".
              if (item.kind === 'spacesInline') {
                if (locked) {
                  return (
                    <div
                      key={item.key}
                      aria-disabled="true"
                      title="Complete payment to unlock"
                      className={
                        'flex items-center gap-3 rounded-[10px] text-[color:var(--color-ink-subtle)] opacity-50 cursor-not-allowed select-none ' +
                        (collapsed ? 'px-0 py-2.5 justify-center' : 'px-3 py-2.5')
                      }
                    >
                      <FolderTree size={18} className="shrink-0" />
                      {collapsed ? null : 'Personal Spaces'}
                    </div>
                  )
                }
                // Collapsed rail: the deep tree is meaningless at icon width.
                // This is the "Spaces" (personal) menu — it must stay
                // independent of Space Explorer, so the icon jumps to the
                // user's first personal Space rather than opening the Explorer
                // modal. No personal Space yet → inert.
                if (collapsed) {
                  const firstPersonal = personalSpaces[0]
                  return (
                    <button
                      key={item.key}
                      type="button"
                      onClick={() =>
                        firstPersonal &&
                        navigate(`/app/spaces/${firstPersonal.id}`)
                      }
                      title="Personal Spaces"
                      aria-label="Personal Spaces"
                      aria-current={onPersonalSpace ? 'page' : undefined}
                      className={
                        'group flex items-center justify-center rounded-[10px] px-0 py-2.5 w-full transition-all cursor-pointer ' +
                        (onPersonalSpace
                          ? 'bg-[color:var(--color-accent-bg-strong)] text-white font-semibold shadow-[inset_0_1px_0_rgba(255,255,255,0.18)]'
                          : 'text-[color:var(--color-ink-muted)] hover:bg-[color:var(--color-card-hover)] hover:text-white')
                      }
                    >
                      <FolderTree size={18} className="shrink-0" />
                    </button>
                  )
                }
                return (
                  <div key={item.key} className="flex flex-col">
                    <button
                      type="button"
                      onClick={() => setSpacesInlineOpen((v) => !v)}
                      // The Personal Spaces tree is NOT one of the accordion
                      // GroupNodes, so force it closed during the tour to honour
                      // the "all dropdowns closed at start" rule (there's no tour
                      // step that opens it).
                      aria-expanded={spacesInlineOpen && !tourActive}
                      // Highlights when the open Space is a personal one (so it
                      // owns the active state, not "Space Explorer" below).
                      aria-current={onPersonalSpace ? 'page' : undefined}
                      className={
                        'group flex items-center gap-3 rounded-[10px] px-3 py-2.5 w-full transition-all cursor-pointer ' +
                        (onPersonalSpace
                          ? 'bg-[color:var(--color-accent-bg-strong)] text-white font-semibold shadow-[inset_0_1px_0_rgba(255,255,255,0.18)]'
                          : 'text-[color:var(--color-ink-muted)] hover:bg-[color:var(--color-card-hover)] hover:text-white')
                      }
                    >
                      <FolderTree size={18} className="shrink-0" />
                      <span className="flex-1 text-left truncate">
                        Personal Spaces
                      </span>
                      <ChevronDown
                        size={14}
                        className={
                          'shrink-0 transition-transform duration-150 ' +
                          (spacesInlineOpen && !tourActive ? '' : '-rotate-90')
                        }
                      />
                    </button>
                    {spacesInlineOpen && !tourActive ? (
                      <AdminSpacesTree collapsed={collapsed} />
                    ) : null}
                  </div>
                )
              }
              if (item.kind === 'spacesTree') {
                if (locked) {
                  return (
                    <div
                      key={item.key}
                      aria-disabled="true"
                      title="Complete payment to unlock"
                      className={
                        'flex items-center gap-3 rounded-[10px] text-[color:var(--color-ink-subtle)] opacity-50 cursor-not-allowed select-none ' +
                        (collapsed ? 'px-0 py-2.5 justify-center' : 'px-3 py-2.5')
                      }
                    >
                      <Layers size={18} className="shrink-0" />
                      {collapsed ? null : 'Space Explorer'}
                    </div>
                  )
                }
                // Spaces entry — single "Space Explorer" button that
                // opens the SpacesModal. When the admin is INSIDE a
                // Space (URL matches `/app/spaces/:id…`), an inline
                // mini-tree renders below it showing the active
                // Workspace → Space → Lists/Folders so the admin
                // gets the same in-sidebar navigation surface that
                // regular users see, without us having to render an
                // always-on tenant-wide tree (which was the original
                // reason the modal pattern was chosen for admins).
                // Space Explorer is "active" any time the admin is
                // somewhere inside the Spaces surface — the standalone
                // Space page (`/app/spaces/:id`) OR a List inside it
                // (`/app/spaces/:id/lists/:lid`). Section header + the
                // tree underneath light up together rather than the
                // leaf List row looking selected on its own.
                //
                // Styles inlined here on purpose: `activeClasses` /
                // `inactiveClasses` constants live inside the
                // `LeafLink` function scope and aren't reachable from
                // the main render. Inlining is the simplest fix
                // without having to hoist + thread two unrelated
                // string constants module-wide.
                // Space Explorer owns DEPARTMENT Spaces only — a personal Space
                // (opened from "Spaces") lights up that section instead.
                const onSpacesSurface = onAnySpace && !onPersonalSpace
                const spacesActiveCls =
                  'bg-[color:var(--color-accent-bg-strong)] text-white font-semibold shadow-[inset_0_1px_0_rgba(255,255,255,0.18)]'
                const spacesInactiveCls =
                  'text-[color:var(--color-ink-muted)] hover:bg-[color:var(--color-card-hover)] hover:text-white'
                return (
                  <div key={item.key} data-tour="nav-spaces" className="flex flex-col">
                    <button
                      type="button"
                      onClick={() => setSpacesModalOpen(true)}
                      title="Space Explorer"
                      // `aria-current="page"` triggers the global
                      // `aside [aria-current='page']::before` rule in
                      // index.css that paints the vertical accent bar
                      // on the outer edge of the row. NavLink sets
                      // this automatically when its `to` matches the
                      // route; this button doesn't have a `to`, so
                      // we set it manually so the active-state bar
                      // matches every other admin nav row.
                      aria-current={onSpacesSurface ? 'page' : undefined}
                      className={
                        'group flex items-center gap-3 rounded-[10px] transition-all cursor-pointer ' +
                        (collapsed
                          ? 'px-0 py-2.5 justify-center w-full '
                          : 'px-3 py-2.5 w-full ') +
                        (onSpacesSurface
                          ? spacesActiveCls
                          : spacesInactiveCls)
                      }
                    >
                      <Layers
                        size={18}
                        strokeWidth={onSpacesSurface ? 2.25 : 1.75}
                        className={
                          'shrink-0 transition-colors ' +
                          (onSpacesSurface
                            ? 'text-white'
                            : 'text-[color:var(--color-ink-muted)] group-hover:text-white')
                        }
                      />
                      {collapsed ? null : (
                        <span className="flex-1 text-left truncate">
                          Space Explorer
                        </span>
                      )}
                    </button>
                    {/* Inline workspace → Space → Project → List tree (the
                     *  original Space Explorer). Renders only when INSIDE a
                     *  department Space and the rail isn't collapsed; the button
                     *  above still opens the department picker modal. */}
                    {!collapsed ? <AdminCurrentSpaceTree /> : null}
                  </div>
                )
              }
              // While locked, every entry except Billing is greyed out
              // and non-interactive.
              if (locked && !isBilling) {
                return item.kind === 'group' ? (
                  <DisabledGroup key={item.key} group={item} collapsed={collapsed} />
                ) : (
                  <DisabledLeaf key={item.to} {...item} collapsed={collapsed} />
                )
              }
              if (item.kind === 'group') {
                return (
                  <GroupNode
                    key={item.key}
                    group={item}
                    currentPath={location.pathname}
                    collapsed={collapsed}
                    open={tourActive ? tourActiveGroup === item.key : openGroup === item.key}
                    onToggle={() =>
                      setOpenGroup((prev) =>
                        prev === item.key ? null : item.key,
                      )
                    }
                  />
                )
              }
              return <LeafLink key={item.to} {...item} collapsed={collapsed} />
            })}
        </nav>
      </aside>

      {/* Main — white canvas */}
      <div className="flex-1 flex flex-col min-w-0 text-[color:var(--color-ink)]">
        {/* Top bar */}
        <header
          className="h-[64px] border-b border-[color:var(--color-rule)] px-4 lg:px-8 flex items-center justify-between gap-2 bg-[color:var(--color-card)]"
          style={{ fontSize: 'calc(var(--text-sm) + 1px)' }}
        >
          {/* Left group — live platform clock pinned to the far left so
           *  the eye reads the current date/time first. The (currently
           *  hidden) language selector also lives here as a global,
           *  app-wide control, separate from the right-side cluster. */}
          <div className="flex min-w-0 items-center gap-2 sm:gap-4">
            {/* Hamburger — opens the off-canvas sidebar drawer. Mobile /
             *  tablet only; hidden on desktop (lg+) where the sidebar is
             *  always in-flow. */}
            <button
              type="button"
              onClick={openMobile}
              aria-label="Open menu"
              className="lg:hidden -ml-1 inline-flex h-9 w-9 items-center justify-center rounded-md text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)] cursor-pointer"
            >
              <Menu size={20} />
            </button>
            {/* Live date/time pill. Hidden on mobile / tablet — it's the
             *  widest header item and purely informational; shown on
             *  desktop (lg+) exactly as before so the header can't overflow
             *  on small screens. */}
            <div className="hidden lg:block">
              <DateTimeClock />
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-1.5 sm:gap-4">
            {/* Quick-create (+) button intentionally hidden on the
             *  Company Admin layout — the admin's job is to manage
             *  Workspaces / Departments / Members / SOPs etc., not
             *  to spin up Spaces / Tasks day-to-day. Each admin
             *  page exposes its own "Add X" CTA. The button still
             *  ships on UserLayout for regular users. */}
            {/* Accessibility text-size control — Compact / Medium /
             *  Large. Sits before the bell so the more frequently
             *  used notifications + profile controls stay in the
             *  rightmost slots where the eye expects them. */}
            <ThemeToggle />
            <FontSizeMenu />
            {/* Notifications bell — surfaces system + peer-to-peer
             *  events for the Company Admin. The dropdown lists the
             *  same payload that lives in /notifications; full triage
             *  is still available via the bell row's deep-link or the
             *  user's Inbox page. */}
            <NotificationsBell />
            {/* Company switcher — the company pill is a dropdown of
             *  every company this person belongs to, so they can switch
             *  organisation in one click. Sits right after the bell,
             *  beside their own profile menu. */}
            <OrganisationSwitcher />
            <div className="h-8 w-px bg-[color:var(--color-rule)]" aria-hidden="true" />
            {user ? <UserMenu user={user} onSignOut={handleSignOut} /> : null}
          </div>
        </header>

        {/* Outer scroll container with consistent padding. Content fills
         *  the full canvas — no max-width cap — so dashboards on ultrawide
         *  displays don't leave huge gutters. Pages keep their own grids
         *  / max-widths internally where it matters. */}
        <main
          ref={mainRef}
          className={
            'app-main-scroll flex-1 bg-[color:var(--color-bg)] relative ' +
            (showLock ? 'overflow-hidden' : 'overflow-auto')
          }
        >
          <div className="px-4 py-6 sm:px-6 lg:px-8 lg:py-8">
            {/* Scope a page-render crash to the CONTENT area only, so one bad
             *  page never takes the sidebar (Space Explorer, nav) down with it.
             *  Keyed by route so navigating away remounts + recovers. */}
            <ErrorBoundary key={pathname}>
              <Outlet />
            </ErrorBoundary>
          </div>
          {showLock ? (
            <BillingLockOverlay billing={billing} user={user} />
          ) : null}
        </main>
      </div>

      {/* Spaces modal — single instance per layout. Portals itself to
       *  document.body so its z-index escapes the sidebar's stacking
       *  context. State is just open/closed; the modal owns its own
       *  search + expanded-set state internally. */}
      <SpacesModal
        open={spacesModalOpen}
        onClose={() => setSpacesModalOpen(false)}
      />

      {/* First-login reminder to set the company timezone — shows once per
       *  session for a Company Admin whose tenant has no zone yet, and
       *  auto-hides the moment one is saved. */}
      <CompanyTimezonePrompt />

      {/* Complete Profile modal — scoped to the Company Admin panel
       *  only. Rendered when a Google-bootstrapped CA hasn't filled
       *  in Company Name + Contact Number yet. The modal's
       *  `fixed inset-0 z-[200]` overlay blocks every sidebar /
       *  topbar / Outlet click, so a partial profile can't navigate
       *  anywhere until the form submits successfully. Self-hides
       *  when `profileComplete` flips to true on the next /auth/me
       *  poll. */}
      {needsCompleteProfile ? <CompleteProfileModal /> : null}
      {/* First-login guided product tour (Company Admin / Sub Admin). Renders
       *  nothing unless the user's persisted tour state is not_started, or they
       *  explicitly restart it from the user menu / User Manual. */}
      <ProductTour />
    </div>
  )
}
